import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["data-handler-id", "draggable"]

import { computed, reactive, ref, unref, onUnmounted } from 'vue'
import { useDrag, useDrop } from 'vue3-dnd'
import type { XYCoord, Identifier } from 'dnd-core'
import { toRefs, useThrottleFn } from '@vueuse/core'
import { DragItemTypes } from './ItemTypes'

const EDGE_ZONE = 60;
const BEYOND_BOOST = 2.5;
const MIN_SPEED = 1.5;
const MAX_SPEED = 12;

interface DragItem {
  index: number
  id: string
  type: string
  callbackParams: any
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DndReorderElement',
  props: {
    id: {},
    index: {},
    callbackParams: {},
    type: {},
    intoAllowed: { type: Boolean },
    maxHeight: {},
    pointerMode: { type: Boolean },
    highlightCondition: { type: Function },
    hoverCondition: { type: Function },
    CtcDropCondition: { type: Function },
    CtrDropCondition: { type: Function },
    RtrDropCondition: { type: Function }
  },
  emits: ["changePosition", "changeParent", "cardMoved", "scrollViewport"],
  setup(__props: any, { emit: __emit }) {

/**
 * Drag-and-drop reorder element for lists where items are reordered
 * by position (columns list, formulas list).
 * Shows an indicator line between elements on hover.
 * В pointer-режиме превью нет, ориентиром служат линия-индикатор и подсветка цели.
 */

let _scrollRaf: number | null = null;
let _scrollSpeed = 0;
let _scrollDir = 0;
let _scrollViewport: HTMLElement | null = null;
let _scrollLastUpdate = 0;

function edgeScrollTick() {
  if (!_scrollViewport || _scrollSpeed === 0 || Date.now() - _scrollLastUpdate > 150) {
    _scrollRaf = null;
    _scrollSpeed = 0;
    _scrollDir = 0;
    return;
  }
  _scrollViewport.scrollTop += _scrollDir * _scrollSpeed;
  _scrollRaf = requestAnimationFrame(edgeScrollTick);
}

function updateEdgeScroll(mouseY: number, viewport: HTMLElement | null) {
  if (!viewport) { stopEdgeScroll(); return; }
  const vr = viewport.getBoundingClientRect();
  const distTop = mouseY - vr.top;
  const distBottom = vr.bottom - mouseY;

  if (distTop < EDGE_ZONE) {
    _scrollDir = -1;
    const ratio = 1 - Math.max(0, distTop) / EDGE_ZONE;
    _scrollSpeed = MIN_SPEED + (MAX_SPEED - MIN_SPEED) * ratio;
    if (distTop < 0) _scrollSpeed *= BEYOND_BOOST;
  } else if (distBottom < EDGE_ZONE) {
    _scrollDir = 1;
    const ratio = 1 - Math.max(0, distBottom) / EDGE_ZONE;
    _scrollSpeed = MIN_SPEED + (MAX_SPEED - MIN_SPEED) * ratio;
    if (distBottom < 0) _scrollSpeed *= BEYOND_BOOST;
  } else {
    _scrollSpeed = 0;
    _scrollDir = 0;
  }

  _scrollViewport = viewport;
  _scrollLastUpdate = Date.now();
  if (_scrollSpeed > 0 && !_scrollRaf) {
    _scrollRaf = requestAnimationFrame(edgeScrollTick);
  }
}

function stopEdgeScroll() {
  _scrollSpeed = 0;
  _scrollDir = 0;
  if (_scrollRaf) { cancelAnimationFrame(_scrollRaf); _scrollRaf = null; }
}

const props = __props

const pointerMode = computed(() => !!props.pointerMode);

const emit = __emit

const moveCard = (params: any) => {
  useThrottleFn((params: any) => { emit('cardMoved', params) }, 100)(params)
}

const throttledPosition = useThrottleFn((params: any) => {
  emit('changePosition', params)
}, 1000, true, false)

const throttledParentChange = useThrottleFn((params: any) => {
  emit('changeParent', params)
}, 1000, true, false)

const signalOnDrop = (moved_item: string, old_pos: number, new_pos: number, dragParams: any) => {
  throttledPosition({ item_id: moved_item, old_pos, new_pos, dragParams })
}

const signalOnCTRDrop = (moved_item: string, new_parent: any, new_pos: number, 
  dragParams: any, hoverParams: any) => {
  throttledParentChange({ item_id: moved_item, new_parent, new_pos, dragParams, hoverParams })
}

const card = ref<HTMLDivElement>()
const [dropCollect, drop] = useDrop<
  DragItem,
  void,
  { handlerId: Identifier | null, isOverCurrent: boolean }
>({
  accept: props.type === DragItemTypes.CARD_ROOT
    ? [ DragItemTypes.CARD_ROOT, DragItemTypes.CARD_CHILD ]
    : DragItemTypes.CARD_CHILD,
  collect(monitor) {
    return {
      handlerId: monitor.getHandlerId(),
      isOverCurrent: monitor.isOver({ shallow: true })
    }
  },
  hover(item: DragItem, monitor) {
    if (!card.value) {
      return
    }

    const offset = monitor.getClientOffset();
    if (offset && props.callbackParams.viewport) {
      updateEdgeScroll(offset.y, props.callbackParams.viewport);
    }

    const highlightCond = props.highlightCondition({
      type: item.type,
      callbackParams: item.callbackParams
    }, {
      type: props.type,
      callbackParams: props.callbackParams,
      id: props.id
    })

    const isRootToRoot = props.RtrDropCondition({
      type: item.type,
      callbackParams: item.callbackParams
    }, {
      type: props.type,
      callbackParams: props.callbackParams,
      id: props.id,
    })

    const allowCond = props.hoverCondition({
      type: item.type,
      callbackParams: item.callbackParams
    }, {
      type: props.type,
      callbackParams: props.callbackParams
    })

    const dragIndex = item.index
    const hoverIndex = props.index

    const hoverBoundingRect = card.value?.getBoundingClientRect()
    const hoverMiddleY = (props.maxHeight ? props.maxHeight : (hoverBoundingRect.bottom - hoverBoundingRect.top)) / 2
    const clientOffset = monitor.getClientOffset()
    const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

    if (highlightCond) {
      if (props.intoAllowed && !isRootToRoot && (
        hoverClientY < hoverMiddleY && hoverMiddleY - hoverClientY < 10 || 
        hoverClientY > hoverMiddleY && hoverClientY - hoverMiddleY < 10)
      ) {
        hoverMode.between = false
        hoverMode.into = true
      } else {
        hoverMode.between = true
        hoverMode.into = false

        if (hoverClientY < hoverMiddleY) {
          hoverMode.is_first = true;
          hoverMode.index_to = dragIndex < hoverIndex ? hoverIndex-1 : hoverIndex
        } else {
          hoverMode.is_first = false;
          hoverMode.index_to = dragIndex < hoverIndex ? hoverIndex : hoverIndex+1
        }
      }

      hoverMode.parentDifferent = item.callbackParams.parent_id !== props.callbackParams.parent_id
    }

    if (!isOverCurrent) {
      return
    }
    if (!allowCond) {
      return
    }

    if (dragIndex === hoverIndex) {
      return
    }

    if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
      return
    }
    if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
      return
    }

    signalOnDrop(item.id, dragIndex, hoverIndex, item.callbackParams)
    moveCard({
      dragIndex,
      hoverIndex,
      parent_id: item.callbackParams.parent_id,
      parent_type: item.callbackParams.parent_type,
      type: item.callbackParams.elType,
    })
    item.index = hoverIndex
  },

  drop(item: DragItem, monitor) {
    if (!monitor.isOver({ shallow: true })) {
      return
    }

    const isChildToChild = props.CtcDropCondition({
      type: item.type,
      callbackParams: item.callbackParams
    }, {
      type: props.type,
      callbackParams: props.callbackParams,
      id: props.id,
    })

    const isChildToRoot = props.CtrDropCondition({
      type: item.type,
      callbackParams: item.callbackParams
    }, {
      type: props.type,
      callbackParams: props.callbackParams,
      id: props.id,
    })

    const isRootToRoot = props.RtrDropCondition({
      type: item.type,
      callbackParams: item.callbackParams
    }, {
      type: props.type,
      callbackParams: props.callbackParams,
      id: props.id,
    })

    if (!isChildToChild && !isChildToRoot && !isRootToRoot) {
      return
    }

    if (hoverMode.between) {
      const dragIndex = item.index
      const hoverIndex = hoverMode.index_to
      if (!hoverMode.parentDifferent &&
          dragIndex === hoverIndex) {
        return
      }

      if (isChildToChild || isRootToRoot) {
        if (!hoverMode.parentDifferent) {
          signalOnDrop(item.id, dragIndex, hoverIndex, item.callbackParams)
          item.index = hoverIndex
        } else {
          signalOnCTRDrop(item.id, props.callbackParams.parent_id, hoverIndex, item.callbackParams, props.callbackParams)
        }
      }
    } else {
      signalOnCTRDrop(item.id, props.id, 0, item.callbackParams, props.callbackParams)
    }
  }
})

const [collect, drag] = useDrag({
  type: props.type,
  item: () => {
    return {
      id: props.id,
      index: props.index,
      type: props.type,
      callbackParams: props.callbackParams
    }
  },
  collect: (monitor: any) => ({
    isDragging: monitor.isDragging(),
  }),
})

const { handlerId, isOverCurrent } = toRefs(dropCollect)
const { isDragging } = toRefs(collect)
const hoverMode = reactive({ between: false, into: false, index_to: 0, is_first: false, parentDifferent: false })
const opacity = computed(() => (unref(isDragging) ? 0 : 1))
const setRef = (el: HTMLDivElement) => {
  card.value = drag(drop(el)) as HTMLDivElement
}

onUnmounted(() => { stopEdgeScroll(); })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref: (setRef as any),
    class: _normalizeClass([ `wrapper`, `card${props.callbackParams.isRoot ? '--root' : ''}`,
      { highlighted: _unref(isOverCurrent) && hoverMode.into, 'wrapper--no-select': pointerMode.value,
        'wrapper--dragged': pointerMode.value && _unref(isDragging) } ]),
    style: _normalizeStyle({ opacity: opacity.value }),
    "data-handler-id": _unref(handlerId),
    draggable: !pointerMode.value && !props.callbackParams.isEditing
  }, [
    _renderSlot(_ctx.$slots, "default"),
    (_unref(isOverCurrent) && !_unref(isDragging) && hoverMode.between)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(['indicator', { first: hoverMode.is_first }, { outer: hoverMode.parentDifferent }])
        }, null, 2))
      : _createCommentVNode("", true)
  ], 14, _hoisted_1))
}
}

})